import { SingleValueData, UTCTimestamp } from "lightweight-charts";
import { cssColor } from "../../utils/themeColor";

// Direction colors for the lightweight-charts canvas (can't parse var()-based
// COLORS.up/down).
export const upColor = (a?: number) => cssColor("--color-up", "22 199 132", a);
export const downColor = (a?: number) => cssColor("--color-down", "234 57 67", a);

export function buildChartData(
  timestampPriceData: string[][],
  syntheticChangePercent?: number
): SingleValueData[] {
  // Process and deduplicate chart data
  let chartData: SingleValueData[] = timestampPriceData
    .slice()
    .map((d) => {
      let timestamp = Number(d[0]);
      // Convert nanoseconds to seconds if timestamp is too large (> year 3000 in seconds)
      if (timestamp > 32503680000) {
        // If in nanoseconds (18+ digits), convert to seconds
        if (timestamp > 1e15) {
          timestamp = Math.floor(timestamp / 1e9);
        }
        // If in milliseconds (13 digits), convert to seconds
        else if (timestamp > 1e12) {
          timestamp = Math.floor(timestamp / 1e3);
        }
      }
      return {
        time: timestamp as UTCTimestamp,
        value: Number(d[1]),
      };
    })
    // Sort by time ascending
    .sort((a, b) => (a.time as number) - (b.time as number));

  // Remove duplicate timestamps (keep last value for each timestamp)
  const seenTimes = new Map<number, SingleValueData>();
  for (const item of chartData) {
    seenTimes.set(item.time as number, item);
  }
  chartData = Array.from(seenTimes.values()).sort(
    (a, b) => (a.time as number) - (b.time as number)
  );

  // Minimum 2 dots
  if (chartData.length === 1) {
    chartData = [
      {
        time: ((chartData[0].time as number) - 1) as UTCTimestamp,
        value: chartData[0].value,
      },
      ...chartData,
    ];
  }

  // If data is flat (all same price) and we have a syntheticChangePercent,
  // generate a synthetic sparkline showing the correct up/down direction.
  const isFlat =
    chartData.length <= 1 ||
    chartData.every((d) => d.value === chartData[0].value);
  if (
    isFlat &&
    syntheticChangePercent !== undefined &&
    chartData.length > 0
  ) {
    const endPrice = chartData[chartData.length - 1].value;
    const startPrice = endPrice / (1 + syntheticChangePercent);
    const now = Math.floor(Date.now() / 1000);
    const dayAgo = now - 86400;
    const points = 20;
    chartData = Array.from({ length: points }, (_, i) => ({
      time: (dayAgo +
        Math.floor((i / (points - 1)) * 86400)) as UTCTimestamp,
      value: startPrice + (endPrice - startPrice) * (i / (points - 1)),
    }));
  }

  return chartData;
}
